import dayjs from 'dayjs';
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosResponse,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import { AppDispatch } from 'store/store';
import { Config, AppRoutes } from 'config';
import { parseAxiosError } from 'utils/error';
import { setLoading } from 'store/slices/loader';
import { getToken, saveTokens } from 'utils/storage';
import { logout, updateAuthUser, setUpdatedToken } from 'store/slices/auth';

import { URL } from './constants';

const { opsURL, baseURL } = Config;

export const apiInstance = axios.create({ baseURL });
export const apiLoaderInstance = axios.create({ baseURL });
export const opsApiInstance = axios.create({ baseURL: opsURL });
export const quickBooksApiInstance = axios.create({
  baseURL,
  timeout: 15_000,
});
export const quickBooksSessionStatusInstance = axios.create({
  baseURL,
  timeout: 5_000,
  validateStatus: () => true,
});

const defaultHeaders: Record<string, any> = {};

const maxRequestRetryAttempts = 1;

const wafIntegrationPollDelayMs = 100;
const wafIntegrationMaxAttempts = 10;
const wafTokenHeaderKey = 'x-aws-waf-token';

const skipRefreshUrls = [URL.LOGIN, URL.LOGOUT, URL.REFRESH];
const logoutOn401Urls: string[] = [];

type RetriableAxiosConfig = InternalAxiosRequestConfig & {
  _retryCount?: number;
};

type QuickBooksAxiosConfig = RetriableAxiosConfig & {
  _qbDeadlineAt?: number;
  _qbAuthRetryCount?: number;
  _qbNetworkRetryCount?: number;
};

export type QuickBooksTransportErrorCode =
  | 'QB_SERVICE_UNAVAILABLE'
  | 'QB_SESSION_CHECK_UNAVAILABLE'
  | 'QB_AUTH_FORWARDING_OR_ROUTE_FAILURE';

export const QUICKBOOKS_CONNECT_TIMEOUT_MS = 3_000;
export const QUICKBOOKS_TOTAL_DEADLINE_MS = 15_000;
export const QUICKBOOKS_SESSION_CHECK_PATH = 'auth/session-status';
// Production Main (master df39b14a) has no auth/session-status route and
// answers it with 404. The QuickBooks transport then asks Main through the
// authenticated read every POS screen already makes (the sidebar's
// company/my-company, preHandler fastify.authenticate). Main -- never the
// QuickBooks worker -- stays the only authority on whether the CLIX session
// has expired, and no Main deploy is needed.
export const QUICKBOOKS_SESSION_FALLBACK_PATH = URL.MY_COMPANY_INFO;

const instances = [apiInstance, apiLoaderInstance];

instances.forEach((instance) => {
  instance.defaults.headers.common = defaultHeaders;
});

const ensureAxiosHeaders = (headers?: RetriableAxiosConfig['headers']) =>
  headers instanceof AxiosHeaders ? headers : new AxiosHeaders(headers ?? {});

const resolveWafIntegration = async () => {
  for (let attempt = 0; attempt < wafIntegrationMaxAttempts; attempt += 1) {
    const wafIntegration =
      typeof window !== 'undefined'
        ? (window as any).AwsWafIntegration
        : undefined;

    if (wafIntegration?.getToken) {
      return wafIntegration;
    }

    if (attempt < wafIntegrationMaxAttempts - 1) {
      await new Promise<void>((resolve) =>
        setTimeout(resolve, wafIntegrationPollDelayMs)
      );
    }
  }

  return undefined;
};

const tryAttachWafToken = async (headers: AxiosHeaders) => {
  if (!Config.waf?.enabled || headers.has(wafTokenHeaderKey)) {
    return;
  }

  const wafIntegration = await resolveWafIntegration();

  if (!wafIntegration?.getToken) {
    return;
  }

  try {
    const wafToken = await wafIntegration.getToken();

    if (wafToken) {
      headers.set(wafTokenHeaderKey, wafToken);
    }
  } catch (e) {
    console.error('cannot get waf token', e);
  }
};

let captchaPromise: Promise<string> | null = null;

const showCaptcha = async () =>
  new Promise<string>((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('window is not defined'));
      return;
    }

    const modalOverlay = document.getElementById('modalOverlay');
    const captcha = document.getElementById('captchaForm');

    if (!modalOverlay || !captcha) {
      reject(new Error('Captcha container not found'));
      return;
    }

    modalOverlay.style.display = 'flex';

    const AwsWafCaptcha = (window as any).AwsWafCaptcha;

    if (!AwsWafCaptcha?.renderCaptcha) {
      modalOverlay.style.display = 'none';
      reject(new Error('AwsWafCaptcha is not available'));
      return;
    }

    AwsWafCaptcha.renderCaptcha(captcha, {
      apiKey: Config.waf.apiKey,
      onError: (err: unknown) => {
        modalOverlay.style.display = 'none';
        reject(err);
      },
      onSuccess: (token: string) => {
        modalOverlay.style.display = 'none';
        resolve(token);
      },
    });
  });

const resolveCaptchaToken = async () => {
  if (!captchaPromise) {
    captchaPromise = showCaptcha()
      .catch((err) => {
        console.error('cannot resolve captcha token', err);
        throw err;
      })
      .finally(() => {
        captchaPromise = null;
      });
  }

  return captchaPromise;
};

const configRequest = async (
  axiosConfig: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  const isRefresh = axiosConfig.url === URL.REFRESH;
  const token = getToken(isRefresh ? 'refreshToken' : 'accessToken');
  const config: RetriableAxiosConfig = { ...axiosConfig };
  const headers = ensureAxiosHeaders(config.headers);

  Object.entries(defaultHeaders).forEach(([key, value]) => {
    headers.set(key, value);
  });

  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  } else {
    headers.delete('authorization');
  }

  await tryAttachWafToken(headers);

  config.headers = headers;

  return config;
};

let refreshPromise: Promise<string> | null = null;

const incrementRetryAndRequest = (
  instance: AxiosInstance,
  request: RetriableAxiosConfig
) => {
  request._retryCount = (request._retryCount ?? 0) + 1;
  return instance.request(request);
};

const refreshAccessToken = async (
  instance: AxiosInstance,
  dispatch: AppDispatch
) => {
  if (!refreshPromise) {
    refreshPromise = instance
      .post(URL.REFRESH, {
        token: getToken('accessToken'),
      })
      .then(({ data }) => {
        const { user, token, refreshToken } = data.data;
        saveTokens({ token, refreshToken });

        dispatch(updateAuthUser(user));
        dispatch(setUpdatedToken(true));

        return token as string;
      })
      .catch((err) => {
        console.error("can't refresh token:", parseAxiosError(err));
        dispatch(setUpdatedToken(false));
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const quickBooksUnavailableError = (
  error: AxiosError,
  code: QuickBooksTransportErrorCode,
  routeRollbackRequired = false
) => {
  const response = error.response;
  error.code = code;
  error.response = {
    status: 503,
    config: error.config!,
    request: response?.request,
    headers: response?.headers ?? {},
    statusText: 'Service Unavailable',
    data: {
      code,
      error: 'QuickBooks service is temporarily unavailable',
      details: {
        routeRollbackRequired,
        localToQuickBooks: true,
      },
    },
  };
  return error;
};

const isQuickBooksNetworkFailure = (error: AxiosError) =>
  !error.response ||
  ['ETIMEDOUT', 'ERR_NETWORK', 'ECONNABORTED', 'ERR_CANCELED'].includes(
    String(error.code || '')
  );

const configureQuickBooksRequest = async (
  axiosConfig: InternalAxiosRequestConfig
) => {
  const config = (await configRequest(axiosConfig)) as QuickBooksAxiosConfig;
  const now = Date.now();
  config._qbDeadlineAt ??= now + QUICKBOOKS_TOTAL_DEADLINE_MS;
  config.timeout = Math.max(1, config._qbDeadlineAt - now);
  return config;
};

const verifyMainSessionByFallbackRead = async () => {
  const response = await quickBooksSessionStatusInstance.get(
    QUICKBOOKS_SESSION_FALLBACK_PATH
  );
  if (response.status === 401) {
    return 401;
  }
  // Only Main's JSON answer proves a live session; an HTML page or an empty
  // body (a wrong origin) proves nothing.
  const isMainJson =
    response.data !== null &&
    typeof response.data === 'object' &&
    !Array.isArray(response.data);
  return response.status >= 200 && response.status < 300 && isMainJson
    ? 204
    : 'unavailable';
};

const verifyMainSession = async () => {
  try {
    const response = await quickBooksSessionStatusInstance.get(
      QUICKBOOKS_SESSION_CHECK_PATH
    );
    if (response.status === 204 || response.status === 401) {
      return response.status;
    }
    if (response.status === 404) {
      return await verifyMainSessionByFallbackRead();
    }
    return 'unavailable';
  } catch {
    return 'unavailable';
  }
};

const handleQuickBooksResponseError = async (
  dispatch: AppDispatch,
  error: AxiosError
) => {
  const request = error.config as QuickBooksAxiosConfig | undefined;
  const method = String(request?.method || 'get').toLowerCase();

  if (isQuickBooksNetworkFailure(error)) {
    const retryCount = request?._qbNetworkRetryCount ?? 0;
    const deadlineRemaining = (request?._qbDeadlineAt ?? 0) - Date.now();
    if (
      request &&
      method === 'get' &&
      retryCount < 1 &&
      deadlineRemaining > 0
    ) {
      request._qbNetworkRetryCount = retryCount + 1;
      return quickBooksApiInstance.request(request);
    }
    return Promise.reject(
      quickBooksUnavailableError(error, 'QB_SERVICE_UNAVAILABLE')
    );
  }

  const status = error.response?.status;
  if ([502, 503, 504].includes(Number(status))) {
    return Promise.reject(
      quickBooksUnavailableError(error, 'QB_SERVICE_UNAVAILABLE')
    );
  }
  if (status !== 401 || !request) {
    return Promise.reject(error);
  }

  const authRetryCount = request._qbAuthRetryCount ?? 0;
  if (authRetryCount < 1 && getToken('refreshToken')) {
    try {
      await refreshAccessToken(apiInstance, dispatch);
      request._qbAuthRetryCount = authRetryCount + 1;
      return quickBooksApiInstance.request(request);
    } catch {
      // Main decides whether the session is truly expired below. A worker or
      // routing failure must never dispatch the global logout by inference.
    }
  }

  const sessionStatus = await verifyMainSession();
  if (sessionStatus === 401) {
    dispatch(logout({ error }));
    dispatch(setUpdatedToken(false));
    return Promise.reject(error);
  }
  if (sessionStatus === 204) {
    return Promise.reject(
      quickBooksUnavailableError(
        error,
        'QB_AUTH_FORWARDING_OR_ROUTE_FAILURE',
        true
      )
    );
  }
  return Promise.reject(
    quickBooksUnavailableError(error, 'QB_SESSION_CHECK_UNAVAILABLE')
  );
};

const validateQuickBooksResponse = (response: AxiosResponse) => {
  if (
    response.status !== 204 &&
    (typeof response.data === 'string' || response.data === undefined)
  ) {
    const error = new AxiosError(
      'QuickBooks upstream returned a malformed response',
      'QB_SERVICE_UNAVAILABLE',
      response.config,
      response.request,
      response
    );
    return Promise.reject(
      quickBooksUnavailableError(error, 'QB_SERVICE_UNAVAILABLE')
    );
  }
  return response;
};

const handleResponseError = async (
  dispatch: AppDispatch,
  instance: AxiosInstance,
  error: AxiosError
) => {
  if (error && error.response) {
    const { config, response } = error;
    const { status } = response;

    switch (status) {
      case 401: {
        const originalRequest = config as RetriableAxiosConfig | undefined;
        const retryCount = originalRequest?._retryCount ?? 0;
        const hasRefreshToken = !!getToken('refreshToken');
        const skipTokenRefresh =
          config?.url !== undefined && skipRefreshUrls.includes(config.url);
        const reachedRetryLimit = retryCount >= maxRequestRetryAttempts;
        const needLogout =
          !hasRefreshToken ||
          reachedRetryLimit ||
          (config?.url && logoutOn401Urls.includes(config.url));

        if (skipTokenRefresh) {
          break;
        } else if (needLogout) {
          dispatch(logout({ error }));
          dispatch(setUpdatedToken(false));
          return Promise.reject(error);
        } else {
          try {
            await refreshAccessToken(instance, dispatch);
          } catch (refreshError) {
            dispatch(logout({ error: refreshError as AxiosError }));
            dispatch(setUpdatedToken(false));
            return Promise.reject(refreshError);
          }

          if (originalRequest) {
            return incrementRetryAndRequest(instance, originalRequest);
          }

          const retriableConfig = config as RetriableAxiosConfig;
          return incrementRetryAndRequest(instance, retriableConfig);
        }
      }

      case 404: {
        if (
          config?.method === 'get' &&
          !config?.url?.includes('talk/') &&
          (!config?.headers ||
            !(config.headers as Record<string, any>)['allow-not-found'])
        ) {
          console.error('not found from', config?.url);
          if (
            typeof window !== 'undefined' &&
            !localStorage.getItem('allow-404')
          ) {
            window.location.href = AppRoutes.NotFound;
          }
        }
        break;
      }

      case 405: {
        if (
          Config.waf?.enabled &&
          error.response &&
          error.response.status === 405 &&
          error.response.headers['x-amzn-waf-action'] === 'captcha'
        ) {
          const originalRequest = config as RetriableAxiosConfig | undefined;
          if (!originalRequest) {
            break;
          }

          let wafToken: string;
          try {
            wafToken = await resolveCaptchaToken();
          } catch (captchaError) {
            return Promise.reject(captchaError);
          }

          const headers = ensureAxiosHeaders(originalRequest.headers);
          headers.set(wafTokenHeaderKey, wafToken);
          originalRequest.headers = headers;

          return incrementRetryAndRequest(instance, originalRequest);
        }

        break;
      }
    }
  }
  return Promise.reject(error);
};

const ignoredURLs = ['v3/team/online'];

const isAllowToLog = (config?: AxiosRequestConfig) => {
  if (!config?.url) {
    return false;
  }
  const ignore = ignoredURLs.find((url) => config.url?.includes(url));
  return !ignore;
};

const getConfigString = (config?: AxiosRequestConfig) =>
  `${dayjs().format(
    'HH:mm:ss.SSS'
  )} | ${config?.method?.toUpperCase()}: ${config?.url}`;

const reportStart = (config: AxiosRequestConfig) => {
  if (isAllowToLog(config)) {
    console.log('started', getConfigString(config));
  }
};

const reportEnd = (response: AxiosResponse) => {
  if (isAllowToLog(response.config)) {
    console.log('finished', getConfigString(response.config));
  }
};

const reportFailed = (error: any) => {
  if (isAllowToLog(error.config)) {
    console.error('finished with error', getConfigString(error.config));
  }
};

apiInstance.interceptors.request.use((config) => {
  reportStart(config);
  return configRequest(config);
});

export const interceptor = (dispatch: AppDispatch) => {
  quickBooksApiInstance.interceptors.request.use(configureQuickBooksRequest);
  quickBooksSessionStatusInstance.interceptors.request.use(configRequest);

  quickBooksApiInstance.interceptors.response.use(
    validateQuickBooksResponse,
    (error) => handleQuickBooksResponseError(dispatch, error)
  );

  apiLoaderInstance.interceptors.request.use(
    (config) => {
      reportStart(config);
      dispatch(setLoading(true));
      return configRequest(config);
    },
    (error) => {
      dispatch(setLoading(false));
      return error;
    }
  );

  apiLoaderInstance.interceptors.response.use(
    (response) => {
      dispatch(setLoading(false));
      reportEnd(response);
      return response;
    },
    (error) => {
      reportFailed(error);
      dispatch(setLoading(false));
      return handleResponseError(dispatch, apiLoaderInstance, error);
    }
  );

  apiInstance.interceptors.response.use(
    (response) => {
      reportEnd(response);
      return response;
    },
    (error) => {
      reportFailed(error);
      return handleResponseError(dispatch, apiInstance, error);
    }
  );
};
